import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Handshake, CheckCircle2, QrCode, Boxes, LogIn, FileDown, Share2, Copy, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { TrackOticBrand } from '@/components/Brand';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import PassportQRPublicPage from '@/pages/public/PassportQRPublicPage';
import { qrGetPublic, qrPostPublic, passportTokenFrom } from './qrPublicApi';
import { LegCard, StatusPill, ScanOnce } from './LegCard';

const TOKEN_RE = /^[0-9A-HJKMNP-TV-Z]{20}$/i;
const deviceTokenFrom = (text) => { const m = /\/d\/([0-9A-HJKMNP-TV-Z]{20})\b/i.exec(text || ''); return m ? m[1].toUpperCase() : (TOKEN_RE.test((text || '').trim()) ? text.trim().toUpperCase() : null); };

const Problem = ({ code, title, text }) => (
  <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: '#FAF9F6' }} data-testid={`passport-problem-${code}`}>
    <div><TrackOticBrand size="xl" /><h1 className="text-2xl font-bold mt-6">{title}</h1><p className="text-sm text-slate-500 mt-2">{text}</p></div>
  </div>
);

const Action = ({ icon: Icon, children, onClick, testId, danger }) => (
  <button data-testid={testId} onClick={onClick} className="w-full rounded-full py-3 font-bold text-white flex items-center justify-center gap-2" style={{ background: danger ? '#B42318' : '#0B6B55' }}>
    <Icon size={18} /> {children}
  </button>
);

/** /p/:token — 20-char Crockford tokens are QR-system passports; anything else is the legacy passport viewer. */
export default function PassportPageRouter() {
  const { passportUuid } = useParams();
  return TOKEN_RE.test(passportUuid || '') ? <PassportPage token={passportUuid.toUpperCase()} /> : <PassportQRPublicPage />;
}

/** /p/:token/certificate.pdf — the API serves the PDF; in environments where only /api reaches the backend we hop there. */
export function CertificateRedirect() {
  const { passportUuid } = useParams();
  useEffect(() => { window.location.replace(`${process.env.REACT_APP_BACKEND_URL}/api/passports/${(passportUuid || '').toUpperCase()}/certificate.pdf`); }, [passportUuid]);
  return <div className="min-h-screen" style={{ background: '#FAF9F6' }} data-testid="certificate-redirect" />;
}

/** Staff share sheet: client-side links to the PUBLIC certificate URL (no login needed by the recipient); logs a SHARE event. */
function ShareSheet({ token, shortCode, onClose }) {
  const url = `${window.location.origin}/p/${token}/certificate.pdf`;
  const text = `Track0tic evidence certificate for passport ${shortCode}: ${url}`;
  const log = (channel) => qrPostPublic('/passports/share-log', { passport_token: token, channel }).catch(() => {});
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch { toast.message(url); } log('copy'); };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose} data-testid="share-sheet">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-2" onClick={e => e.stopPropagation()}>
        <p className="font-semibold">Share certificate</p>
        <p className="text-xs text-slate-500 break-all" data-testid="share-url">{url}</p>
        <button data-testid="share-copy" onClick={copy} className="w-full rounded-full py-2.5 text-sm font-semibold border flex items-center justify-center gap-2" style={{ borderColor: '#D9D6CD' }}><Copy size={14} /> Copy link</button>
        <a data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" onClick={() => log('whatsapp')} className="w-full rounded-full py-2.5 text-sm font-semibold text-white flex items-center justify-center gap-2" style={{ background: '#25D366' }}><MessageCircle size={14} /> WhatsApp</a>
        <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent(`Track0tic certificate ${shortCode}`)}&body=${encodeURIComponent(text)}`} onClick={() => log('email')} className="w-full rounded-full py-2.5 text-sm font-semibold border flex items-center justify-center gap-2" style={{ borderColor: '#D9D6CD' }}><Mail size={14} /> Email</a>
        <button data-testid="share-close" onClick={onClose} className="w-full py-2 text-xs text-slate-500">Close</button>
      </div>
    </div>
  );
}

const CertificateButton = ({ token, cert }) => {
  if (!cert?.available) return null;
  const href = `${process.env.REACT_APP_BACKEND_URL}${cert.url}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid="certificate-download" data-provisional={cert.provisional ? 'true' : 'false'}
       className="w-full rounded-full py-2.5 text-sm font-semibold border bg-white flex items-center justify-center gap-2"
       style={{ borderColor: cert.provisional ? '#E2B93B' : '#D9D6CD', color: cert.provisional ? '#8A6D1F' : '#0B6B55' }}>
      <FileDown size={14} /> {cert.provisional ? 'Download provisional certificate (awaiting device sync)' : 'Download certificate (PDF)'}
    </a>
  );
};

function PassportPage({ token }) {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useTrackOticAuth();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState(null);
  const [confirmHandover, setConfirmHandover] = useState(false);
  const [scan, setScan] = useState(null); // 'device' | 'parent'
  const [share, setShare] = useState(false);

  const load = useCallback(() => qrGetPublic(`/passports/${token}/journey`).then(setJ).catch(setErr), [token]);
  useEffect(() => { if (!authLoading) load(); }, [load, authLoading, user]);

  const geo = () => new Promise(res => navigator.geolocation
    ? navigator.geolocation.getCurrentPosition(p => res({ lat: p.coords.latitude, lon: p.coords.longitude }), () => res({}), { timeout: 6000 }) : res({}));

  const handover = async () => {
    try {
      const g = await geo();
      await qrPostPublic('/handover', { passport_token: token, client_request_id: crypto.randomUUID(), ...g });
      toast.success('Handed over'); setConfirmHandover(false); load();
    } catch (e) { toast.error(e.message); setConfirmHandover(false); }
  };
  const ack = async () => { try { await qrPostPublic('/receipt-ack', { passport_token: token }); toast.success('Receipt acknowledged'); load(); } catch (e) { toast.error(e.message); } };
  const onScan = useCallback(async (text) => {
    const mode = scan; setScan(null);
    try {
      if (mode === 'device') {
        const dt = deviceTokenFrom(text);
        const trk = !dt && /^[A-Z0-9]{2,8}-[A-Z0-9-]{2,}$/i.test((text || '').trim()) ? text.trim().toUpperCase() : null;   // typed Track0tic ID (manual fallback)
        if (!dt && !trk) return toast.error('That is not a device QR or Track0tic ID');
        const g = await geo();
        const r = await qrPostPublic('/bind', { device_token: dt, device_trk_id: trk, passport_token: token, client_request_id: crypto.randomUUID(), lat: g.lat ?? null, lon: g.lon ?? null });
        toast.success(r.already_here ? 'Already on that device' : `Bound${r.moved_from ? ` — moved from ${r.moved_from}` : ''}`);
      } else {
        const pt = passportTokenFrom(text); if (!pt) return toast.error('That is not a passport QR');
        const r = await qrPostPublic(`/passports/${token}/parent`, { parent_token: pt });
        toast.success(`Now inside ${r.parent_short_code}`);
      }
      load();
    } catch (e) { toast.error(e.message); }
  }, [scan, token, load]);

  if (err?.loginRequired) return <Navigate to={`/d-login?next=${encodeURIComponent(`/p/${token}`)}`} replace />;
  if (err?.status === 404) return <Problem code="404" title="Not a valid Track0tic code" text="This QR code is not recognised." />;
  if (err?.status === 429) return <Problem code="429" title="Too many scans" text="Please wait a minute and try again." />;
  if (err) return <Problem code="error" title="Something went wrong" text={err.message} />;
  if (!j) return <div className="min-h-screen" style={{ background: '#FAF9F6' }} />;

  const isPublic = j.viewer.layer === 'public';
  const lastClosed = j.legs.find(l => l.unbound_at);
  return (
    <div className="min-h-screen pb-10" style={{ background: '#FAF9F6' }} data-testid="passport-page">
      <header className="px-4 pt-6 pb-4 text-center">
        <TrackOticBrand size="lg" />
        <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#E7F2EE', color: '#0B6B55' }}><ShieldCheck size={14} /> Track0tic passport</div>
        <h1 className="text-3xl font-bold font-mono mt-3" data-testid="passport-short-code">{j.short_code}</h1>
        <p className="text-sm text-slate-500 mt-1" data-testid="passport-state">{j.state.replace('_', ' ')}{j.parent_short_code ? ` · in ${j.parent_short_code}` : ''}{j.profile ? ` · ${j.profile.name}` : ''}</p>
      </header>
      <main className="max-w-md mx-auto px-4 space-y-4">
        {j.state === 'VOID' && (
          <div className="rounded-xl bg-red-50 text-red-800 p-4 text-sm" data-testid="passport-void">
            <p className="font-semibold">This passport has been cancelled</p>
            {j.void && <p className="mt-1">{j.void.reason} — {j.void.by} · {new Date(j.void.at).toLocaleString('en-IN')}</p>}
          </div>
        )}
        {j.state === 'ISSUED' && <p className="rounded-xl bg-white border p-4 text-sm text-center" style={{ borderColor: '#E5E3DC' }} data-testid="passport-issued">Valid Track0tic passport — not yet activated</p>}
        {isPublic && j.state === 'OFF_DEVICE' && lastClosed && (
          <div className="rounded-xl bg-white border p-4" style={{ borderColor: '#E5E3DC' }} data-testid="evidence-so-far">
            <p className="text-xs text-slate-500">Evidence so far</p>
            <div className="flex items-center gap-2 mt-1"><span className="font-semibold">Last leg</span><StatusPill status={lastClosed.status} /></div>
          </div>
        )}
        {j.state !== 'ISSUED' && j.state !== 'VOID' && <CertificateButton token={token} cert={j.certificate} />}
        {isPublic && j.state !== 'ISSUED' && j.state !== 'VOID' && (
          <button data-testid="passport-login" onClick={() => navigate(`/d-login?next=${encodeURIComponent(`/p/${token}`)}`)} className="w-full rounded-full py-2.5 text-sm font-semibold border bg-white flex items-center justify-center gap-2" style={{ borderColor: '#D9D6CD' }}>
            <LogIn size={14} /> Staff log in
          </button>
        )}

        {!isPublic && j.state !== 'VOID' && (
          <div className="space-y-2" data-testid="passport-actions">
            {j.viewer.can_handover && !confirmHandover && <Action icon={Handshake} testId="handover-btn" onClick={() => setConfirmHandover(true)}>Hand over</Action>}
            {confirmHandover && (
              <div className="rounded-xl bg-white border p-4 space-y-2" style={{ borderColor: '#E5E3DC' }} data-testid="handover-confirm">
                <p className="text-sm font-semibold">Handed over at your current place?</p>
                <div className="flex gap-2"><Action icon={Handshake} testId="handover-confirm-btn" onClick={handover} danger>Yes, handed over</Action>
                  <button data-testid="handover-cancel" onClick={() => setConfirmHandover(false)} className="px-4 rounded-full border text-sm" style={{ borderColor: '#D9D6CD' }}>Cancel</button></div>
              </div>
            )}
            {j.viewer.can_ack && <Action icon={CheckCircle2} testId="ack-btn" onClick={ack}>Acknowledge receipt</Action>}
            {j.certificate?.available && <button data-testid="share-certificate-btn" onClick={() => setShare(true)} className="w-full rounded-full py-2.5 text-sm font-semibold border bg-white flex items-center justify-center gap-2" style={{ borderColor: '#D9D6CD', color: '#0B6B55' }}><Share2 size={14} /> Share certificate</button>}
            {j.viewer.can_bind && <Action icon={QrCode} testId="bind-device-btn" onClick={() => setScan('device')}>Bind to a device</Action>}
            {j.viewer.can_set_parent && <Action icon={Boxes} testId="set-parent-btn" onClick={() => setScan('parent')}>Put inside a carton (scan its passport)</Action>}
          </div>
        )}

        {j.legs.length > 0 && (
          <ol className="relative space-y-3 border-l ml-1.5 pl-0" style={{ borderColor: '#D9D6CD' }} data-testid="passport-timeline">
            {j.legs.map(l => <LegCard key={l.binding_id} leg={l} profile={j.profile} isPublic={isPublic} />)}
          </ol>
        )}
        {!isPublic && (j.handovers.length > 0 || j.receipt_acks.length > 0) && (
          <section className="rounded-xl bg-white border p-4 text-xs space-y-1" style={{ borderColor: '#E5E3DC' }} data-testid="passport-events">
            {[...j.handovers, ...j.receipt_acks].sort((a, b) => a.at.localeCompare(b.at)).map((e, i) => (
              <p key={`${e.at}-${i}`}><span className="font-semibold">{e.kind === 'HANDED_OVER' ? 'Handed over' : 'Receipt acknowledged'}</span> · {e.tenant_name}{e.by ? ` · ${e.by}` : ''} · {new Date(e.at).toLocaleString('en-IN')}</p>
            ))}
          </section>
        )}
      </main>
      {share && <ShareSheet token={token} shortCode={j.short_code} onClose={() => setShare(false)} />}
      {scan && <ScanOnce title={scan === 'device' ? 'Scan the DEVICE QR' : 'Scan the carton passport QR'} onCode={onScan} onClose={() => setScan(null)}
        manualLabel={scan === 'device' ? 'No camera? Type the device Track0tic ID (e.g. TRK-PRO-000011)' : 'No camera? Type the 20-character passport token'}
        manualPlaceholder={scan === 'device' ? 'TRK-PRO-000011' : 'Passport token'} />}
    </div>
  );
}
